import React, { useState, useEffect } from 'react';
import { addDoc, onSnapshot, orderBy, query, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { countdownsColl, countdownDoc } from '../db';
import { UserProfile, Screen } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; backTo?: Screen; }

interface CountdownItem {
  id: string;
  title: string;
  date: string;
  createdBy: string;
  timestamp: any;
}

function getRemaining(dateStr: string) {
  const target = new Date(dateStr).getTime();
  const now = Date.now();
  const diff = target - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds };
}

function CountdownTimer({ dateStr, title }: { dateStr: string; title: string }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const r = getRemaining(dateStr);

  if (!r) {
    return (
      <div style={timerStyles.passed}>
        <span style={{ fontSize: 24 }}>🎉</span>
        <span>{title} oldu!</span>
      </div>
    );
  }

  return (
    <div style={timerStyles.grid}>
      {[
        { val: r.days, label: 'Gün' },
        { val: r.hours, label: 'Saat' },
        { val: r.minutes, label: 'Dk' },
        { val: r.seconds, label: 'Sn' },
      ].map(({ val, label }) => (
        <div key={label} style={timerStyles.block}>
          <span style={timerStyles.num}>{String(val).padStart(2, '0')}</span>
          <span style={timerStyles.label}>{label}</span>
        </div>
      ))}
    </div>
  );
}

const timerStyles: Record<string, React.CSSProperties> = {
  grid: { display: 'flex', gap: 8, justifyContent: 'center' },
  block: {
    background: 'rgba(0,0,0,0.25)', borderRadius: 10, padding: '10px 12px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 56,
  },
  num: {
    fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700,
    background: 'linear-gradient(135deg, #FF6B9D, #C77DFF)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  label: { fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 },
  passed: { display: 'flex', gap: 8, alignItems: 'center', fontSize: 16, color: '#FF6B9D', justifyContent: 'center' },
};

export default function Countdown({ user, navigate, backTo = 'us' }: Props) {
  const [countdowns, setCountdowns] = useState<CountdownItem[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = query(countdownsColl(user.coupleId), orderBy('date', 'asc'));
    return onSnapshot(q, snap => setCountdowns(snap.docs.map(d => ({ id: d.id, ...d.data() } as CountdownItem))));
  }, [user.coupleId]);

  const add = async () => {
    if (!title.trim() || !date) return;
    setSaving(true);
    await addDoc(countdownsColl(user.coupleId), {
      title: title.trim(), date, createdBy: user.uid, timestamp: serverTimestamp(),
    });
    setTitle(''); setDate('');
    setShowAdd(false); setSaving(false);
  };

  const remove = async (id: string) => {
    await deleteDoc(countdownDoc(user.coupleId, id));
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Geri Sayım</h1>
      </div>

      <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {countdowns.map(c => (
          <div key={c.id} style={styles.countCard}>
            <div style={styles.countHeader}>
              <span style={styles.countTitle}>⏰ {c.title}</span>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                  {new Date(c.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <button onClick={() => remove(c.id)} style={styles.deleteBtn}>×</button>
              </div>
            </div>
            <CountdownTimer dateStr={c.date} title={c.title} />
          </div>
        ))}

        {countdowns.length === 0 && (
          <div style={styles.empty}>
            <span style={{ fontSize: 56 }}>⏰</span>
            <p>Geri sayım yok</p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>Bir buluşma tarihi ekle!</p>
          </div>
        )}
      </div>

      <button className="fab" onClick={() => setShowAdd(true)}>+</button>

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 style={{ fontFamily: "'Playfair Display',serif", marginBottom: 16, fontSize: 20 }}>
              ⏰ Geri Sayım Ekle
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input
                type="text"
                placeholder="Ne için? (örn: Buluşma, Tatil...)"
                value={title}
                onChange={e => setTitle(e.target.value)}
              />
              <div>
                <label style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 6 }}>Tarih</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} />
              </div>
              <button className="btn btn-primary" onClick={add} disabled={saving || !title.trim() || !date}>
                {saving ? '⏳ Kaydediliyor...' : '💾 Ekle'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Playfair Display',serif", fontSize: 22 },
  countCard: {
    background: 'linear-gradient(135deg, rgba(233,30,140,0.1), rgba(156,39,176,0.1))',
    border: '1px solid rgba(233,30,140,0.2)',
    borderRadius: 20, padding: 18,
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  countHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  countTitle: { fontSize: 15, fontWeight: 600 },
  deleteBtn: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: 22, cursor: 'pointer' },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 8, padding: '60px 20px', color: 'rgba(255,255,255,0.5)', fontSize: 15, textAlign: 'center',
  },
};
